import { isNil, map, sortBy } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { shopGroupsApi } from 'api';
import { FormikSelect, FormikSelectProps } from 'components';
import { QueryKey } from 'enums';
import { useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { ShopGroup } from 'types';
import { formatUtils } from 'utils';

type Props = {
  name: string;
  label?: string;
  shopGroups?: ShopGroup[];
} & Partial<FormikSelectProps>;

export const ShopGroupSelect: React.FC<Props> = ({
  name,
  label,
  shopGroups,
  ...rest
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.shop_group_select',
  });
  const { role } = useUser();

  const queryResult = useQuery(
    QueryKey.ShopGroups,
    shopGroupsApi.getAllAsRole(role),
    {
      enabled: isNil(shopGroups),
    },
  );

  const options = useMemo(
    () =>
      map(
        sortBy(shopGroups || queryResult?.data, ['archived', 'name']),
        (group) => ({
          value: group.id,
          label: formatUtils.formatName(group) || group.id,
        }),
      ),
    [shopGroups, queryResult],
  );

  return (
    <FormikSelect
      noneOption
      label={label || t('label')}
      {...rest}
      name={name}
      options={options}
    />
  );
};
